import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../services/CustomerApiNew';
import logo from '../logo/mb_logo_100x100.png';
import './MainPage.css';
import './ProviderPage.css';

const API_BASE_URL = '/api/v1';

const isCustomerLoggedIn = () => localStorage.getItem('customerAccessToken') !== null;

const StarRating = ({ value, onChange, readOnly = false, size = 'md' }) => {
	const stars = [1, 2, 3, 4, 5];
	return (
		<div className={`star-rating star-rating--${size}`}>
			{stars.map((star) => (
				<button
					key={star}
					type="button"
					className={`star ${star <= value ? 'star--filled' : ''}`}
					onClick={() => !readOnly && onChange && onChange(star)}
					disabled={readOnly}
					aria-label={`${star} star`}
				>
					★
				</button>
			))}
		</div>
	);
};

const Header = () => {
	const navigate = useNavigate();
	return (
		<header className="header">
			<div className="logo-container" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
				<img src={logo} alt="Logo" className="logo-img" />
				<div className="logo-text">
					<span className="logo-orange">MYBHARAT</span>
					<span className="logo-blue">SERVICE</span>
				</div>
			</div>
			<nav>
				<a href="/register">Professionals Registration</a>
				&nbsp; | &nbsp;
				<a href="/login">Login</a>
				&nbsp; &nbsp; &nbsp; &nbsp;
				<a href="/customer-login">Customer Login</a>
			</nav>
		</header>
	);
};

const ReviewForm = ({ providerId, existingReview, onSubmitted }) => {
	const navigate = useNavigate();
	const [rating, setRating] = useState(existingReview?.rating || 0);
	const [comment, setComment] = useState(existingReview?.comment || '');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');

	useEffect(() => {
		if (existingReview) {
			setRating(existingReview.rating);
			setComment(existingReview.comment || '');
		}
	}, [existingReview]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!isCustomerLoggedIn()) {
			navigate('/customer-login');
			return;
		}
		if (rating < 1) {
			setError('Please select a rating.');
			return;
		}

		setSubmitting(true);
		setError('');
		try {
			await api.post(`${API_BASE_URL}/providers/${providerId}/reviews`, { rating, comment: comment.trim() });
			onSubmitted();
		} catch (err) {
			if (err.response?.status === 401 || err.response?.status === 403) {
				setError('Please log in as a customer to submit a review.');
			} else {
				setError('Failed to submit review. Please try again.');
			}
		} finally {
			setSubmitting(false);
		}
	};

	if (!isCustomerLoggedIn()) {
		return (
			<div className="review-login-prompt">
				<p>Log in to rate and review this provider.</p>
				<Link to="/customer-login" className="btn-primary">Customer Login</Link>
			</div>
		);
	}

	return (
		<form className="review-form" onSubmit={handleSubmit}>
			<h3>{existingReview ? 'Update Your Review' : 'Write a Review'}</h3>
			<label className="form-label">Your Rating</label>
			<StarRating value={rating} onChange={setRating} />
			<label className="form-label" htmlFor="review-comment">Your Review</label>
			<textarea
				id="review-comment"
				value={comment}
				onChange={(e) => setComment(e.target.value)}
				placeholder="Share your experience with this provider..."
				maxLength={1000}
				rows={4}
			/>
			{error && <p className="error-message">{error}</p>}
			<button type="submit" className="btn-primary" disabled={submitting}>
				{submitting ? 'Submitting...' : existingReview ? 'Update Review' : 'Submit Review'}
			</button>
		</form>
	);
};

function ProviderPage() {
	const { providerId } = useParams();
	const navigate = useNavigate();
	const [provider, setProvider] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [previewPhoto, setPreviewPhoto] = useState(null);

	const loadProvider = async () => {
		setLoading(true);
		setError('');
		try {
			const response = await api.get(`${API_BASE_URL}/providers/${providerId}`);
			setProvider(response.data);
		} catch (err) {
			if (err.response?.status === 404) {
				setError('Provider not found.');
			} else {
				setError('Failed to load provider details.');
			}
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadProvider();
	}, [providerId]);

	useEffect(() => {
		if (!previewPhoto) return undefined;
		const photos = provider?.workPhotos || [];
		const onKeyDown = (e) => {
			if (e.key === 'Escape') setPreviewPhoto(null);
			if (e.key === 'ArrowLeft') {
				const prev = previewPhoto.idx > 0 ? previewPhoto.idx - 1 : photos.length - 1;
				setPreviewPhoto({ photo: photos[prev], idx: prev });
			}
			if (e.key === 'ArrowRight') {
				const next = previewPhoto.idx < photos.length - 1 ? previewPhoto.idx + 1 : 0;
				setPreviewPhoto({ photo: photos[next], idx: next });
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [previewPhoto, provider]);

	if (loading) {
		return (
			<div className="main-container">
				<Header />
				<p className="loading-text">Loading provider details...</p>
			</div>
		);
	}

	if (error || !provider) {
		return (
			<div className="main-container">
				<Header />
				<div className="provider-error">
					<p>{error || 'Provider not found.'}</p>
					<button onClick={() => navigate('/')}>Back to Home</button>
				</div>
			</div>
		);
	}

	const serviceCitiesDisplay = Array.isArray(provider.serviceCities)
		? provider.serviceCities.join(' | ')
		: provider.serviceCities;

	return (
		<div className="main-container">
			<Helmet>
				<title>{provider.name} | MyBharatService</title>
				<meta
					name="description"
					content={`View ${provider.name}'s profile, ratings, and reviews on MyBharatService.`}
				/>
			</Helmet>

			<Header />

			<button className="back-link" onClick={() => navigate(-1)}>← Back</button>

			<section className="provider-profile">
				<div className="provider-profile-header">
					<div>
						<h1>{provider.name}</h1>
						<p className="provider-location">{provider.city}{provider.state ? `, ${provider.state}` : ''}</p>
						{provider.serviceType && (
							<span className="provider-badge">{provider.serviceType}</span>
						)}
						{provider.verified && <span className="provider-badge provider-badge--verified">Verified</span>}
						{provider.premium && <span className="provider-badge provider-badge--premium">Premium</span>}
					</div>
					<div className="rating-summary">
						<div className="rating-score">{provider.averageRating ? provider.averageRating.toFixed(1) : '—'}</div>
						<StarRating value={Math.round(provider.averageRating || 0)} readOnly size="lg" />
						<p className="review-count">{provider.reviewCount} review{provider.reviewCount !== 1 ? 's' : ''}</p>
					</div>
				</div>

				<div className="provider-details-grid">
					<div className="detail-item">
						<span className="detail-label">Service Area</span>
						<span className="detail-value">{serviceCitiesDisplay || '—'}</span>
					</div>
					<div className="detail-item">
						<span className="detail-label">Experience</span>
						<span className="detail-value">{provider.experience} years</span>
					</div>
					<div className="detail-item">
						<span className="detail-label">Charge</span>
						<span className="detail-value">₹{provider.charge} {provider.chargeType}</span>
					</div>
				</div>

				{provider.services && (
					<div className="services-section">
						<h2>Services</h2>
						<p>{provider.services}</p>
					</div>
				)}
			</section>

			{provider.workPhotos && provider.workPhotos.length > 0 && (
				<section className="work-portfolio-section">
					<h2>Completed Work</h2>
					<p className="work-portfolio-subtitle">Photos uploaded by the provider to showcase past projects</p>
					<div className="work-portfolio-grid">
						{provider.workPhotos.map((photo, idx) => (
							<button
								key={photo.id}
								type="button"
								className="work-portfolio-item"
								onClick={() => setPreviewPhoto({ photo, idx })}
								title="Click to view full size"
							>
								<img src={photo.url} alt={`${provider.name} completed work`} loading="lazy" />
							</button>
						))}
					</div>
				</section>
			)}

			{previewPhoto && (
				<div className="work-portfolio-preview-overlay" onClick={() => setPreviewPhoto(null)}>
					<div className="work-portfolio-preview-modal" onClick={(e) => e.stopPropagation()}>
						<button
							type="button"
							className="work-portfolio-preview-close"
							onClick={() => setPreviewPhoto(null)}
							aria-label="Close preview"
						>
							×
						</button>

						{provider.workPhotos.length > 1 && (
							<button
								type="button"
								className="lightbox-nav lightbox-nav--prev"
								onClick={(e) => {
									e.stopPropagation();
									const prevIdx = previewPhoto.idx > 0 ? previewPhoto.idx - 1 : provider.workPhotos.length - 1;
									setPreviewPhoto({ photo: provider.workPhotos[prevIdx], idx: prevIdx });
								}}
								aria-label="Previous photo"
							>
								&#8249;
							</button>
						)}

						<img src={previewPhoto.photo.url} alt={`${provider.name} completed work full size`} />

						{provider.workPhotos.length > 1 && (
							<button
								type="button"
								className="lightbox-nav lightbox-nav--next"
								onClick={(e) => {
									e.stopPropagation();
									const nextIdx = previewPhoto.idx < provider.workPhotos.length - 1 ? previewPhoto.idx + 1 : 0;
									setPreviewPhoto({ photo: provider.workPhotos[nextIdx], idx: nextIdx });
								}}
								aria-label="Next photo"
							>
								&#8250;
							</button>
						)}

						<p className="lightbox-counter">{previewPhoto.idx + 1} / {provider.workPhotos.length}</p>
					</div>
				</div>
			)}

			<section className="reviews-section">
				<h2>Reviews</h2>

				<ReviewForm
					providerId={providerId}
					existingReview={provider.myReview}
					onSubmitted={loadProvider}
				/>

				{provider.reviews && provider.reviews.length > 0 ? (
					<div className="reviews-list">
						{provider.reviews.map((review) => (
							<article key={review.id} className="review-card">
								<div className="review-card-header">
									<strong>{review.customerName || 'Customer'}</strong>
									<StarRating value={review.rating} readOnly size="sm" />
								</div>
								{review.comment && <p className="review-comment">{review.comment}</p>}
								<time className="review-date">
									{new Date(review.createdAt).toLocaleDateString('en-IN', {
										year: 'numeric', month: 'short', day: 'numeric'
									})}
								</time>
							</article>
						))}
					</div>
				) : (
					<p className="no-reviews">No reviews yet. Be the first to review!</p>
				)}
			</section>
		</div>
	);
}

export default ProviderPage;
